import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import apiRequest from '../utils/api';

interface ReportData {
  totalOrders: number;
  totalRevenue: number;
  totalProfit: number;
  avgOrderValue: number;
  completionRate: number;
  topServices: Array<{
    serviceId: number;
    count: number;
    revenue: number;
  }>;
  dailyStats: Array<{
    date: string;
    orders: number;
    revenue: number;
    profit: number;
  }>;
  statusBreakdown: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  serviceStats: Array<{
    serviceId: number;
    count: number;
    revenue: number;
    cost: number;
    profit: number;
  }>;
}

const Reports: React.FC = () => {
  const { hasAnyPermission } = useAuth();
  const [reportData, setReportData] = useState<ReportData>({
    totalOrders: 0,
    totalRevenue: 0,
    totalProfit: 0,
    avgOrderValue: 0,
    completionRate: 0,
    topServices: [],
    dailyStats: [],
    statusBreakdown: [],
    serviceStats: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiRequest(`/api/reports?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        const errorText = await response.text();
        console.error('Reports API error:', response.status, errorText);
        throw new Error(`Không thể tải dữ liệu báo cáo (${response.status}): ${errorText}`);
      }
    } catch (error: any) {
      console.error('Reports fetch error:', error);
      setError(error.message || 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const dt = new Date(dateString);
    const pad = (n: number) => String(n).padStart(2, '0');
    const d = pad(dt.getUTCDate());
    const m = pad(dt.getUTCMonth() + 1);
    const y = dt.getUTCFullYear();
    return `${d}/${m}/${y}`;
  };

  const StatCard: React.FC<{
    title: string;
    value: string;
    icon: string;
    bgColor: string;
    change?: string;
    changeColor?: string;
  }> = ({ title, value, icon, bgColor, change, changeColor = '#16a34a' }) => (
    <div style={{
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #f1f5f9'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{
            fontSize: '14px',
            color: '#64748b',
            margin: '0 0 8px 0',
            fontWeight: '500'
          }}>
            {title}
          </p>
          <p style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1e293b',
            margin: '0 0 4px 0'
          }}>
            {value}
          </p>
          {change && (
            <p style={{
              fontSize: '12px',
              color: changeColor,
              margin: 0,
              fontWeight: '500'
            }}>
              {change}
            </p>
          )}
        </div>
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: bgColor,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px'
        }}>
          {icon}
        </div>
      </div>
    </div>
  );

  // Check if user has permission for reports module
  if (!hasAnyPermission('reports')) {
    return (
      <Layout currentPage="reports">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{ fontSize: '48px' }}>🔒</div>
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#374151',
            textAlign: 'center'
          }}>
            Không có quyền truy cập
          </div>
          <div style={{
            fontSize: '14px',
            color: '#6b7280',
            textAlign: 'center'
          }}>
            Bạn không có quyền truy cập module này. Vui lòng liên hệ quản trị viên.
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout currentPage="reports">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
          <div className="loading-spinner" style={{ width: '40px', height: '40px' }}></div>
        </div>
      </Layout>
    );
  }


  return (
    <Layout currentPage="reports">
      <div style={{
        width: '100%',
        maxWidth: '100%'
      }}>
        {error && (
          <div style={{
            backgroundColor: '#fecaca',
            border: '1px solid #f87171',
            color: '#dc2626',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Date Range Filter */}
        <div style={{
          backgroundColor: 'white',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Từ ngày:
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              style={{
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Đến ngày:
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              style={{
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
          <button
            onClick={fetchReportData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
              marginTop: '20px'
            }}
          >
            🔄 Cập nhật
          </button>
        </div>

        {/* Main Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <StatCard
            title="Tổng đơn hàng"
            value={reportData.totalOrders.toLocaleString()}
            icon="📦"
            bgColor="#dbeafe"
          />
          <StatCard
            title="Tổng doanh thu"
            value={formatCurrency(reportData.totalRevenue)}
            icon="💰"
            bgColor="#dcfce7"
          />
          <StatCard
            title="Tổng lợi nhuận"
            value={formatCurrency(reportData.totalProfit)}
            icon="📈"
            bgColor="#fef3c7"
          />
          <StatCard
            title="Giá trị đơn hàng TB"
            value={formatCurrency(reportData.avgOrderValue)}
            icon="📊"
            bgColor="#e0e7ff"
          />
        </div>

        {/* Performance Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <StatCard
            title="Tỷ lệ hoàn thành"
            value={`${reportData.completionRate.toFixed(1)}%`}
            icon="✅"
            bgColor="#dcfce7"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
          {/* Status Breakdown */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #f1f5f9'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #f1f5f9'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                margin: 0
              }}>
                Phân bố trạng thái đơn hàng
              </h3>
            </div>
            <div style={{ padding: '20px' }}>
              {reportData.statusBreakdown.map((item, index) => (
                <div key={item.status} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: index === reportData.statusBreakdown.length - 1 ? 'none' : '1px solid #f1f5f9'
                }}>
                  <span style={{ fontSize: '14px', color: '#374151' }}>
                    {item.status === 'pending' && 'Chờ xử lý'}
                    {item.status === 'inprogress' && 'Đang xử lý'}
                    {item.status === 'completed' && 'Hoàn thành'}
                    {item.status === 'pause' && 'Tạm dừng'}
                    {item.status === 'refund' && 'Hoàn tiền'}
                    {item.status === 'wait_refund' && 'Chờ hoàn tiền'}
                    {item.status === 'special' && 'Đặc biệt'}
                    {item.status === 'processing' && 'Đang xử lý'}
                    {item.status === 'failed' && 'Thất bại'}
                    {item.status === 'cancelled' && 'Đã hủy'}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>
                      {item.count}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      minWidth: '40px',
                      textAlign: 'right'
                    }}>
                      ({item.percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Services */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #f1f5f9'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #f1f5f9'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                margin: 0
              }}>
                Top dịch vụ
              </h3>
            </div>
            <div style={{ padding: '20px' }}>
              {reportData.topServices.slice(0, 5).map((service, index) => (
                <div key={service.serviceId} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: index === Math.min(reportData.topServices.length, 5) - 1 ? 'none' : '1px solid #f1f5f9'
                }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                      Dịch vụ #{service.serviceId}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {service.count} đơn hàng
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                      {formatCurrency(service.revenue)}
                    </div>
                  </div>
                </div>
              ))}
              {reportData.topServices.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  color: '#9ca3af',
                  fontSize: '14px',
                  padding: '20px'
                }}>
                  Chưa có dữ liệu dịch vụ
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Per-Service Detailed Stats Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #f1f5f9',
          marginBottom: '32px'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1e293b',
              margin: 0
            }}>
              📊 Chi tiết doanh thu & lợi nhuận theo dịch vụ
            </h3>
            <span style={{
              fontSize: '13px',
              color: '#6b7280',
              backgroundColor: '#f1f5f9',
              padding: '4px 10px',
              borderRadius: '12px'
            }}>
              {reportData.serviceStats.length} dịch vụ
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', whiteSpace: 'nowrap' }}>#</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', whiteSpace: 'nowrap' }}>Service ID</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#475569', whiteSpace: 'nowrap' }}>Đơn hàng</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#475569', whiteSpace: 'nowrap' }}>Doanh thu</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#475569', whiteSpace: 'nowrap' }}>Chi phí (API)</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#475569', whiteSpace: 'nowrap' }}>Lợi nhuận</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#475569', whiteSpace: 'nowrap' }}>Biên lợi nhuận</th>
                </tr>
              </thead>
              <tbody>
                {reportData.serviceStats.map((service, index) => {
                  const margin = service.revenue > 0 ? (service.profit / service.revenue) * 100 : 0;
                  const isNegativeProfit = service.profit < 0;
                  return (
                    <tr key={service.serviceId} style={{
                      borderBottom: '1px solid #f1f5f9',
                      backgroundColor: index % 2 === 0 ? 'white' : '#fafbfc',
                      transition: 'background-color 0.15s ease'
                    }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f0f9ff'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = index % 2 === 0 ? 'white' : '#fafbfc'; }}
                    >
                      <td style={{ padding: '10px 16px', fontSize: '13px', color: '#94a3b8', fontWeight: '500' }}>
                        {index + 1}
                      </td>
                      <td style={{ padding: '10px 16px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                        #{service.serviceId}
                      </td>
                      <td style={{ padding: '10px 16px', fontSize: '14px', fontWeight: '500', color: '#374151', textAlign: 'right' }}>
                        {service.count.toLocaleString()}
                      </td>
                      <td style={{ padding: '10px 16px', fontSize: '14px', fontWeight: '600', color: '#059669', textAlign: 'right' }}>
                        {formatCurrency(service.revenue)}
                      </td>
                      <td style={{ padding: '10px 16px', fontSize: '14px', fontWeight: '500', color: '#6b7280', textAlign: 'right' }}>
                        {formatCurrency(service.cost)}
                      </td>
                      <td style={{
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '700',
                        color: isNegativeProfit ? '#dc2626' : '#2563eb',
                        textAlign: 'right'
                      }}>
                        {formatCurrency(service.profit)}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: isNegativeProfit ? '#fef2f2' : margin >= 50 ? '#f0fdf4' : margin >= 20 ? '#eff6ff' : '#fefce8',
                          color: isNegativeProfit ? '#dc2626' : margin >= 50 ? '#16a34a' : margin >= 20 ? '#2563eb' : '#ca8a04'
                        }}>
                          {margin.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {reportData.serviceStats.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '14px'
                    }}>
                      Chưa có dữ liệu dịch vụ trong khoảng thời gian này
                    </td>
                  </tr>
                )}
              </tbody>
              {reportData.serviceStats.length > 0 && (
                <tfoot>
                  <tr style={{ backgroundColor: '#f1f5f9', borderTop: '2px solid #e2e8f0' }}>
                    <td colSpan={2} style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>
                      Tổng cộng ({reportData.serviceStats.length} dịch vụ)
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '700', color: '#374151', textAlign: 'right' }}>
                      {reportData.serviceStats.reduce((s, i) => s + i.count, 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '700', color: '#059669', textAlign: 'right' }}>
                      {formatCurrency(reportData.serviceStats.reduce((s, i) => s + i.revenue, 0))}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '700', color: '#6b7280', textAlign: 'right' }}>
                      {formatCurrency(reportData.serviceStats.reduce((s, i) => s + i.cost, 0))}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '700', color: '#2563eb', textAlign: 'right' }}>
                      {formatCurrency(reportData.serviceStats.reduce((s, i) => s + i.profit, 0))}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '700', color: '#475569', textAlign: 'right' }}>
                      {(() => {
                        const totalRev = reportData.serviceStats.reduce((s, i) => s + i.revenue, 0);
                        const totalPr = reportData.serviceStats.reduce((s, i) => s + i.profit, 0);
                        return totalRev > 0 ? `${((totalPr / totalRev) * 100).toFixed(1)}%` : '0.0%';
                      })()}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Daily Stats Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #f1f5f9'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #f1f5f9'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1e293b',
              margin: 0
            }}>
              Thống kê theo ngày
            </h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Ngày</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Đơn hàng</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Doanh thu</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Lợi nhuận</th>
                </tr>
              </thead>
              <tbody>
                {reportData.dailyStats.map((day, index) => (
                  <tr key={day.date} style={{
                    borderBottom: index === reportData.dailyStats.length - 1 ? 'none' : '1px solid #f1f5f9'
                  }}>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#374151' }}>
                      {formatDate(day.date)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#1f2937', textAlign: 'right' }}>
                      {day.orders}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#059669', textAlign: 'right' }}>
                      {formatCurrency(day.revenue)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#2563eb', textAlign: 'right' }}>
                      {formatCurrency(day.profit)}
                    </td>
                  </tr>
                ))}
                {reportData.dailyStats.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '14px'
                    }}>
                      Chưa có dữ liệu trong khoảng thời gian này
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Reports;